// <HrmezPopup/> — the H.R.M.E.Z quick-capture pad as a floating popup that
// opens over any screen without navigating away (mobile analogue of the web
// HrmezWidget floating panel). Wraps the full native HrmezTool in a slide-up
// sheet with its own close affordance.
import React from 'react';
import { Modal, Pressable, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { X } from 'lucide-react-native';
import { useTheme, makeThemedStyles, DISPLAY_FONT, radii } from '../v10/theme';
import { HrmezTool } from '../tools/HrmezTool';
import { GlassFill } from './glass';

export function HrmezPopup({
  open,
  onClose,
  onOpenTool,
}: {
  open: boolean;
  onClose: () => void;
  onOpenTool?: (toolId: string) => void;
}) {
  const { t } = useTheme();
  const styles = useStyles();
  return (
    <Modal visible={open} transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <View style={styles.sheet}>
          <GlassFill strong radius={radii.panel} />
          <SafeAreaView edges={['top']} style={styles.safe}>
            <View style={styles.header}>
              <Text style={styles.title}>H.R.M.E.Z</Text>
              <Pressable onPress={onClose} style={styles.close} hitSlop={8}>
                <X size={20} color={t.ivory} strokeWidth={2} />
              </Pressable>
            </View>
            <View style={styles.body}>
              <HrmezTool sector="H" stage="T" gear="regulate" onOpenTool={onOpenTool} />
            </View>
          </SafeAreaView>
        </View>
      </View>
    </Modal>
  );
}

const useStyles = makeThemedStyles(({ t, c, neu }) =>
  StyleSheet.create({
    backdrop: { flex: 1, backgroundColor: c.overlay, justifyContent: 'flex-end' },
    sheet: {
      backgroundColor: 'transparent',
      borderTopLeftRadius: radii.panel,
      borderTopRightRadius: radii.panel,
      borderTopWidth: 1,
      borderColor: t.navyLine,
      height: '92%',
      overflow: 'hidden',
      ...neu.raisedLg,
    },
    safe: { flex: 1 },
    // No header bar: a bare centred title, close floats over the corner.
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 14,
    },
    title: { fontSize: 17, fontWeight: '900', letterSpacing: 2.5, color: t.ivory, fontFamily: DISPLAY_FONT, textTransform: 'uppercase' },
    close: {
      position: 'absolute',
      right: 16,
      top: 12,
      width: 32,
      height: 32,
      borderRadius: 16,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: t.glass,
      zIndex: 2,
    },
    body: { flex: 1 },
  }),
);
